import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Avatar, Confirm, Panel, Stat, Title, ago, uxd } from "@/components/admin/kit";

const TONE = { waiting: "bg-amber-100 text-amber-800", open: "bg-amber-100 text-amber-800", done: "bg-emerald-100 text-emerald-800" };

// b is null while a challenge waits for an answer, and the harbour master ({k: null}) in a quick match against the house.
const name = (p) => (!p ? "nobody yet" : p.k === null ? "the house" : `@${p.h}`);

const Who = ({ p }) => (!p ? <span className="text-slate-400">nobody</span> : (
  <span className="inline-flex items-center gap-1.5">
    <Avatar src={p.a} alt={`@${p.h}`} size={24} />
    {p.k === null ? <span className="font-semibold">The house</span> : <a href={`/?at=${p.h}`} className="font-semibold hover:underline">@{p.h}</a>}
  </span>
));

export function AdminDuelsPanel() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api("/admin/duels").then(setD).catch((e) => toast.error(e.message)), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  const act = (path, done) => api(path, { method: "POST" }).then((r) => { toast.success(done(r)); load(); }).catch((e) => toast.error(e.message));
  return (
    <div className="space-y-5" data-testid="duels-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Recent duels" value={d.duels.length} sub="newest first" testId="duels-count" />
        <Stat label="Flagged" value={d.flagged} sub="marked suspicious" testId="duels-flagged" />
        <Stat label="Saved runs" value={d.runs} sub="recorded plays" testId="duels-runs" />
        <div className="flex items-center">
          <Confirm title={`Clear all ${d.runs} saved runs?`} action="Clear saved runs" disabled={!d.runs} testId="duels-clear-runs"
            text="Every saved run is deleted. This can't be undone. Duels and their results stay as they are."
            onConfirm={() => act("/admin/runs/clear", (r) => `Cleared ${r.cleared} saved runs`)}>Clear saved runs</Confirm>
        </div>
      </div>
      <Panel testId="duels-table-panel">
        <Title hint="Voiding a duel that hasn't finished refunds whatever is in escrow. Finished duels can't be voided.">Harbour Rush duels</Title>
        {!d.duels.length ? <div className="text-sm text-slate-500" data-testid="duels-empty">No duels yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="duels-table">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="pb-2">Players</th><th className="pb-2">Stake</th><th className="pb-2">Score</th><th className="pb-2">Status</th><th className="pb-2">Winner</th><th className="pb-2">When</th><th /></tr>
              </thead>
              <tbody>
                {d.duels.map((x) => (
                  <tr key={x.id} className={`border-t border-slate-100 align-middle ${x.flagged ? "bg-rose-50/60" : ""}`} data-testid={`duel-row-${x.id}`}>
                    <td className="py-2.5 pr-3">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1"><Who p={x.a} /><span className="text-slate-400">vs</span><Who p={x.b} /></div>
                      <span className="text-xs text-slate-500">{x.kind}</span>
                    </td>
                    <td className="py-2.5 pr-3 tabular-nums">{uxd(x.stake)}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{x.a_score ?? "–"} : {x.b_score ?? "–"}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[x.status] || "bg-slate-100 text-slate-600"}`}>{x.status}</span>
                      {x.flagged && <div className="mt-1 text-xs font-semibold text-rose-700" data-testid={`duel-flag-${x.id}`}>Flagged{x.reason ? `: ${x.reason}` : ""}</div>}
                    </td>
                    <td className="py-2.5 pr-3">{x.winner_h ? <a href={`/?at=${x.winner_h}`} className="font-semibold hover:underline">@{x.winner_h}</a> : <span className="text-slate-400">{x.status !== "done" ? "–" : x.a_score === x.b_score ? "tie" : "the house"}</span>}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{ago(x.created_at)}</td>
                    <td className="py-2.5 text-right">
                      {(x.status === "waiting" || x.status === "open") && (
                        <Confirm title="Void this duel?" action="Void and refund" className="px-2.5 py-1.5" testId={`duel-void-${x.id}`}
                          text={`The duel between ${name(x.a)} and ${name(x.b)} for ${uxd(x.stake)} is cancelled and whatever is in escrow goes back to the players.`}
                          onConfirm={() => act(`/admin/duels/${x.id}/void`, () => "Duel voided and refunded")}>Void + refund</Confirm>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
